import { useRef } from 'react';
import Router from 'next/router';
import { Modal } from '../../Modal';
import { contactThreadPath } from '../../../lib/contact-book';
import { formatFeedTime } from '../../../lib/home3-feed';
import { useTranslation } from '../../../lib/i18n';
import { messageDir } from '../../../lib/message-direction';
import {
  OUTREACH_CARD_STATUS,
  outreachCardActions,
  outreachCardPerson,
  outreachCardStatus,
  outreachCardSubtitle,
  type OutreachCardActions,
} from '../../../lib/outreach-card';
import type { SentOutreachSummary } from '../../../lib/outreach-rows';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { Badge, Button, Typography } from '../ui';
import type { OutreachCardContext } from './use-outreach-card-context';
import { threadStepperPosition } from '../../../lib/outreach-thread-stepper';
import { ThreadStepper, useScrollTopOnStep, type ThreadSibling } from '../outreach/ThreadStepper';
import styles from './OutreachCardModal.module.css';

/** A display word for the channel the thread went out on. */
function channelWord(raw: string | undefined): string {
  const value = (raw ?? '').trim().toUpperCase();
  if (!value) return '';
  if (value === 'WHATSAPP') return 'WhatsApp';
  if (value === 'SMS') return 'SMS';
  if (value === 'EMAIL' || value === 'GMAIL_CONNECTOR') return 'Email';
  if (value === 'APP') return 'App';
  return value.charAt(0) + value.slice(1).toLowerCase();
}

/** Runs the card's actions the same way from the row and from the detail. */
export function useOutreachCardActionRunner(page: string) {
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  return (actions: OutreachCardActions, which: 'reply' | 'openChat', outreachId: string) => {
    logPageClickBi(
      { page_name: page, button_name: which === 'reply' ? 'outreach_card_reply' : 'outreach_card_open_chat' },
      { componentName: 'outreach-card', additionalFields: { outreach_id: outreachId } },
    );
    if (which === 'reply' && actions.reply) {
      // The thread screen with its "Tell {agent}" composer — the existing owner-reply path.
      const path = contactThreadPath(actions.reply.contact, actions.reply.outreachId);
      void Router.push(path, `${basePath}${path}`);
      return;
    }
    if (actions.openChat) {
      // The lead's owner-side conversation — where the Profile-links People row opens too.
      const params = new URLSearchParams({ conversation: actions.openChat.conversationId, chat: 'fullscreen' });
      window.location.assign(`${basePath}/?${params.toString()}`);
    }
  };
}

/** The card's actions for one thread. */
export function cardActionsFor(summary: Pick<SentOutreachSummary, 'id'>, ctx: OutreachCardContext): OutreachCardActions {
  return outreachCardActions({
    outreachId: summary.id,
    personId: ctx.personId,
    recipientId: ctx.recipientId,
    conversationId: ctx.lead?.conversationId,
  });
}

/**
 * The outreach card's detail (home Recent activity → a Messages row). Header: the PERSON (never "Recipient"),
 * where they came from and the true status; then the conversation as a chat — their words labelled with their
 * name, ours as "Your AI" (never "<name>'s agent" for a person) — and the card's actions.
 * Mirrors iOS/Android `OutreachCardSheet`.
 */
export function OutreachCardModal({
  summary,
  ctx,
  onClose,
  page,
  siblings,
}: {
  summary: SentOutreachSummary;
  ctx: OutreachCardContext;
  onClose: () => void;
  page: string;
  /** The opener's thread rows in on-screen order, for Previous / Next (`ThreadStepper`). Absent → none. */
  siblings?: readonly ThreadSibling[];
}) {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useScrollTopOnStep(bodyRef, summary.id);
  const { t } = useTranslation();
  const run = useOutreachCardActionRunner(page);
  const messages = (summary.messages ?? []).map((m) => ({ mine: m.mine, text: m.text, at: m.at }));
  const person = outreachCardPerson({ recipientName: summary.recipientName, contactName: ctx.contactName, leadName: ctx.lead?.name }) || t('contacts.row.unnamed');
  const status = outreachCardStatus({ messages, stageGroup: ctx.stageGroup ?? null, leadNeedsYou: ctx.lead?.needsYou, awaitingReply: summary.awaitingReply });
  const view = OUTREACH_CARD_STATUS[status];
  const subtitle = outreachCardSubtitle(status, messages);
  const subtitleText = t(subtitle.key, subtitle.time ? { time: formatFeedTime(Date.parse(subtitle.time)) } : undefined);
  const origin = [ctx.lead ? t('outreachCard.origin.link', { link: ctx.lead.linkLabel }) : '', channelWord(summary.channel)].filter(Boolean).join(' · ');
  const actions = cardActionsFor(summary, ctx);

  const footer =
    actions.reply || actions.openChat ? (
      <div className={styles.actions}>
        {actions.openChat && (
          <Button variant={actions.reply ? 'outlined' : 'contained'} onClick={() => { onClose(); run(actions, 'openChat', summary.id); }}>
            {t('outreachCard.action.openChat')}
          </Button>
        )}
        {actions.reply && (
          <Button onClick={() => { onClose(); run(actions, 'reply', summary.id); }}>{t('outreachCard.action.reply')}</Button>
        )}
      </div>
    ) : undefined;

  return (
    <Modal
      open
      onClose={onClose}
      title={person}
      maxHeightPx={650}
      scrollBody
      footer={footer}
      // The same stepper the received-thread dialog carries, in the shell's trailing header slot.
      headerActions={threadStepperPosition(siblings, summary.id) ? <ThreadStepper siblings={siblings} currentId={summary.id} page={{ page_name: page }} /> : undefined}
    >
      <div className={styles.head} ref={bodyRef}>
        {origin ? (
          <Typography variant="p2" color="secondary" component="span">
            {origin}
          </Typography>
        ) : null}
        <Badge variant={view.variant} size="tiny" label={t(view.labelKey)} />
      </div>
      <Typography variant="p2" color="primary" component="p" className={styles.subtitle}>
        {subtitleText}
      </Typography>
      {messages.length === 0 ? (
        <Typography variant="p2" color="secondary" component="p">
          {t('tasks.outreach.emptyThread')}
        </Typography>
      ) : (
        <ol className={styles.thread}>
          {messages.map((m, i) => {
            // A label opens each run of one speaker.
            const opensRun = i === 0 || messages[i - 1]?.mine !== m.mine;
            const text = m.text ?? '';
            return (
              <li key={i} className={m.mine ? `${styles.message} ${styles.mine}` : styles.message}>
                {opensRun ? (
                  <Typography variant="p3" color="secondary" component="span">
                    {m.mine ? t('outreachCard.speaker.yourAi') : person}
                  </Typography>
                ) : null}
                <Typography variant="p2" color="primary" component="p" className={styles.text} dir={messageDir(text)}>
                  {text}
                </Typography>
                {m.at ? (
                  <Typography variant="p3" color="secondary" component="span" title={new Date(m.at).toLocaleString()}>
                    {formatFeedTime(Date.parse(m.at))}
                  </Typography>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </Modal>
  );
}
